import { Menu } from '@mantine/core';
import { getFileTypeLabel } from 'src/data-capture/utils/documentCategory';
import { formatDate } from 'src/meetings/utils/date-utils';
import { DetectedClient, UploadedFile } from '../../hooks/file-uploads/types';
import DocumentCard, { DocumentCardTag } from './DocumentCard';
import EditDocumentDateChip from './EditDocumentDateChip';
import StatusBar from './StatusBar';

export type FiledInfo = {
  fileId: string;
  folderName: string;
  count: number;
  folderId: string | null;
};

const isPendingFile = (file: UploadedFile) => file.id.startsWith('pending-');

type AddDocumentCardProps = {
  file: UploadedFile;
  filedInfo: FiledInfo | undefined;
  rootFolderLabel: string;
  contactName: string | null;
  onDownload: (file: UploadedFile) => void;
  onDelete: (file: UploadedFile) => void;
  onUpdateDate: (file: UploadedFile, date: Date) => void;
  onProcess: (file: UploadedFile, clientId?: string) => void;
  onRetry?: (file: UploadedFile) => void;
  isRetrying?: boolean;
  onFileTo: (file: UploadedFile) => void;
  onOpenFiledFolder: (info: FiledInfo) => void;
  onDismissDetectedClient?: (file: UploadedFile, clientId: string) => void;
  dismissingClientId?: string | null;
};

export default function AddDocumentCard({
  file,
  filedInfo,
  rootFolderLabel,
  contactName,
  onDownload,
  onDelete,
  onUpdateDate,
  onProcess,
  onRetry,
  isRetrying,
  onFileTo,
  onOpenFiledFolder,
  onDismissDetectedClient,
  dismissingClientId,
}: AddDocumentCardProps) {
  const status = file.backgroundProcess?.status;
  const isExtracting = status === 'PENDING' || status === 'PROCESSING';
  const isReady = status === 'SUCCESS' && !file.isResolved;
  const isReadyToFile = status === 'SUCCESS' && file.isResolved && !file.file.isFiled;
  const isPending = isPendingFile(file);

  const typeLabel = getFileTypeLabel(file);
  const createdLabel = `Created: ${file.documentDate ? formatDate(file.documentDate) : 'no date detected'}`;
  const createdTag: DocumentCardTag = isPending
    ? { label: createdLabel }
    : {
        label: createdLabel,
        node: (
          <EditDocumentDateChip
            label={createdLabel}
            value={file.documentDate ? new Date(file.documentDate) : null}
            maxDate={new Date(file.createdAt)}
            onChange={date => onUpdateDate(file, date)}
          />
        ),
      };
  const tags: DocumentCardTag[] = [
    ...(typeLabel ? [{ label: typeLabel }] : []),
    createdTag,
    { label: `Uploaded: ${formatDate(file.createdAt)}` },
  ];

  return (
    <DocumentCard
      fileName={file.file.fileName}
      tags={tags}
      extracting={isExtracting}
      menuItems={
        isPending ? undefined : (
          <>
            <Menu.Item onClick={() => onDownload(file)}>Download document</Menu.Item>
            <Menu.Item onClick={() => onDelete(file)}>Delete</Menu.Item>
          </>
        )
      }
    >
      {isExtracting && <StatusBar variant="extracting" />}
      {status === 'FAILED' && (
        <StatusBar
          variant="error"
          onRetry={onRetry ? () => onRetry(file) : undefined}
          isRetrying={isRetrying}
        />
      )}
      {isReady &&
        (file.detectedClients.length > 0
          ? file.detectedClients.map(client => (
              <DetectedClientRow
                key={client.id}
                client={client}
                onProcess={() => onProcess(file, client.id)}
                onDismiss={
                  onDismissDetectedClient
                    ? () => onDismissDetectedClient(file, client.id)
                    : undefined
                }
                isDismissing={dismissingClientId === client.id}
              />
            ))
          : contactName && (
              <StatusBar
                variant="ready"
                contactName={contactName}
                onProcess={() => onProcess(file)}
              />
            ))}
      {filedInfo ? (
        <StatusBar
          variant="filed"
          folderName={filedInfo.folderName}
          count={filedInfo.count}
          onOpen={() => onOpenFiledFolder(filedInfo)}
        />
      ) : (
        isReadyToFile && (
          <StatusBar
            variant="fileTo"
            rootFolderLabel={rootFolderLabel}
            onFileTo={() => onFileTo(file)}
          />
        )
      )}
    </DocumentCard>
  );
}

function DetectedClientRow({
  client,
  onProcess,
  onDismiss,
  isDismissing,
}: {
  client: DetectedClient;
  onProcess: () => void;
  onDismiss?: () => void;
  isDismissing: boolean;
}) {
  const contactName = `${client.firstName} ${client.lastName}`;

  if (client.isProcessed) {
    return <StatusBar variant="done" contactName={contactName} />;
  }

  return (
    <StatusBar
      variant="ready"
      contactName={contactName}
      onProcess={onProcess}
      onDismiss={onDismiss}
      isDismissing={isDismissing}
    />
  );
}
